import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { Users, Loader2, Check } from "lucide-react";
import { OfficialBearInsiderTooltip } from "@/components/OfficialBearInsiderTooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface CommunityShareButtonProps {
  storyId: number;
  isOwner: boolean;
}

export function CommunityShareButton({ storyId, isOwner }: CommunityShareButtonProps) {
  const { user, isOfficialBearInsider } = useAuth();
  const { toast } = useToast();
  const [isShared, setIsShared] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const shareMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/stories/${storyId}/share`);
      return response.json();
    },
    onSuccess: () => {
      setIsShared(true);
      toast({
        title: "Story shared!",
        description: "Your story is now visible in the community section.",
      });
      setIsOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to share",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const unshareMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/stories/${storyId}/share`);
      return response.json();
    },
    onSuccess: () => {
      setIsShared(false);
      toast({
        title: "Story removed from community",
        description: "Your story is no longer visible in the community section.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to remove",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (!user || !isOwner) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="gradient" size="sm" className="text-white">
          <Users className="w-4 h-4 mr-2" />
          {isShared ? "Shared" : "Share to Community"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Share to Community
            {isOfficialBearInsider && (
              <OfficialBearInsiderTooltip showShield={true} size="small">
                <span />
              </OfficialBearInsiderTooltip>
            )}
          </DialogTitle>
          <DialogDescription>
            {isShared
              ? "This story is already shared with the community. Would you like to remove it?"
              : <>Share this story with the <span className='text-snuggle-gradient'>SnuggleCrafters</span> community! Other users will be able to read and vote on your story.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="gradient" onClick={() => setIsOpen(false)} className="text-white">
            Cancel
          </Button>
          {isShared ? (
            <button
              onClick={() => unshareMutation.mutate()}
              disabled={unshareMutation.isPending}
              className="px-4 py-2 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg hover:from-red-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 flex items-center"
            >
              {unshareMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                "Remove from Community"
              )}
            </button>
          ) : (
            <button
              onClick={() => shareMutation.mutate()}
              disabled={shareMutation.isPending}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-teal-600 text-white rounded-lg hover:from-purple-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 flex items-center"
            >
              {shareMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sharing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Share Story
                </>
              )}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}